import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Skeleton } from './ui/skeleton';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ConsultantBrandingSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);
  
  const [branding, setBranding] = useState({
    branding_logo_url: '',
    branding_color_primary: '#10B981',
    branding_company_name: '',
    report_footer_text: ''
  });
  
  const [useUpload, setUseUpload] = useState(true); // Toggle between upload and URL

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      setError('');

      const config = {
        withCredentials: true
      };

      const response = await axios.get(`${API_URL}/api/consultant/branding`, config);
      
      if (response.data.branding && Object.keys(response.data.branding).length > 0) {
        setBranding({
          branding_logo_url: response.data.branding.branding_logo_url || '',
          branding_color_primary: response.data.branding.branding_color_primary || '#10B981',
          branding_company_name: response.data.branding.branding_company_name || '',
          report_footer_text: response.data.branding.report_footer_text || ''
        });
      }
    } catch (err) {
      logger.error('Error fetching branding:', err);
      setError(err.response?.data?.detail || 'Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess('');
    setError('');
  };
  
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPEG, or WebP)');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    
    await uploadLogo(file);
  };
  
  const uploadLogo = async (file) => {
    try {
      setUploading(true);
      setError('');
      setSuccess('');
      
      const formData = new FormData();
      formData.append('file', file);
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      };
      
      const response = await axios.post(
        `${API_URL}/api/consultant/branding/upload-logo`,
        formData,
        config
      );
      
      // Check if upload was successful
      if (response.data.success) {
        // Update branding with uploaded logo URL (Supabase Storage)
        setBranding(prev => ({
          ...prev,
          branding_logo_url: response.data.logo_url
        }));
        
        setSuccess('Logo uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to upload logo');
      }
    } catch (err) {
      logger.error('Error uploading logo:', err);
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      };

      // Build update payload - only include logo URL if using external URL mode
      const updatePayload = {
        branding_color_primary: branding.branding_color_primary,
        branding_company_name: branding.branding_company_name,
        report_footer_text: branding.report_footer_text
      };
      
      // Only include logo URL if we're in URL mode (not upload mode)
      if (!useUpload && branding.branding_logo_url) {
        updatePayload.branding_logo_url = branding.branding_logo_url;
      }

      await axios.put(
        `${API_URL}/api/consultant/branding`,
        updatePayload,
        config
      );

      setSuccess('Branding settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logger.error('Error saving branding:', err);
      setError(err.response?.data?.detail || 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };
  
  const getEffectiveLogoUrl = () => {
    return branding.branding_logo_url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header Skeleton */}
          <div className="mb-8">
            <Skeleton className="h-10 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>

          {/* Form Skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Skeleton className="h-6 w-48" />
            </div>
            <div className="p-6 space-y-6">
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-32 w-full" />
              </div>
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <div className="flex space-x-4">
                  <Skeleton className="h-12 w-20" />
                  <Skeleton className="h-10 flex-1" />
                </div>
              </div>
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex justify-end space-x-4">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-32" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header with Back Button */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link
              to="/consultant/dashboard"
              className="mr-4 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">White-Label Branding</h1>
              <p className="text-gray-600 dark:text-gray-400">
                Customize your PDF reports with your company's branding
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 dark:text-green-300">{success}</p>
          </div>
        )}

        {/* Branding Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Brand Identity</h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company Name <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={branding.branding_company_name}
                onChange={(e) => handleChange('branding_company_name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Your Company Name"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This will appear in the report header and footer
              </p>
            </div>

            {/* Logo Upload/URL Toggle */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company Logo
                </label>
                <button
                  type="button"
                  onClick={() => setUseUpload(!useUpload)}
                  className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {useUpload ? 'Use Logo URL instead' : 'Upload Logo instead'}
                </button>
              </div>
              
              {useUpload ? (
                // Upload Option
                <div>
                  <div 
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-emerald-500 dark:hover:border-emerald-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500 mb-3"></div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Uploading...</p>
                      </div>
                    ) : (
                      <div>
                        <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-emerald-600 dark:text-emerald-400">Click to upload</span> or drag and drop
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                          PNG, JPEG, or WebP (Max 5MB)
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    ✅ Recommended: Uploaded logos are stored securely and load reliably in PDFs
                  </p>
                </div>
              ) : (
                // URL Option
                <div>
                  <input
                    type="url"
                    value={branding.branding_logo_url}
                    onChange={(e) => handleChange('branding_logo_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    ⚠️ External URLs may not load in PDFs. Upload is recommended for reliability.
                  </p>
                </div>
              )}
              
              {/* Logo Preview */}
              {getEffectiveLogoUrl() && (
                <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Logo Preview:</p>
                  <img
                    src={getEffectiveLogoUrl()}
                    alt="Company Logo"
                    className="h-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <p className="text-xs text-red-600 dark:text-red-400 hidden mt-2">
                    Unable to load logo. Please check the URL or try uploading instead.
                  </p>
                  {getEffectiveLogoUrl() && getEffectiveLogoUrl().includes('supabase.co/storage') && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      ✅ Using uploaded logo (Supabase Storage)
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Brand Color
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="color"
                  value={branding.branding_color_primary}
                  onChange={(e) => handleChange('branding_color_primary', e.target.value)}
                  className="h-12 w-20 rounded-lg cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={branding.branding_color_primary}
                  onChange={(e) => handleChange('branding_color_primary', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="#10B981"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Used for headers, buttons, and accent elements in reports
              </p>
            </div>

            {/* Footer Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Report Footer Text
              </label>
              <textarea
                value={branding.report_footer_text}
                onChange={(e) => handleChange('report_footer_text', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                rows={3}
                placeholder="e.g., Confidential - For Internal Use Only"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional custom text to display at the bottom of each report page
              </p>
            </div>
          </div>

          {/* Preview Section */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</h3>
            <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 p-6">
              {/* Header Preview */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                {getEffectiveLogoUrl() ? (
                  <img
                    src={getEffectiveLogoUrl()}
                    alt="Logo"
                    className="h-12 object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="h-12 w-32 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                    Logo
                  </div>
                )}
                <div className="text-right">
                  <h4 className="font-bold text-gray-900 dark:text-white">
                    {branding.branding_company_name || 'Company Name'}
                  </h4>
                </div>
              </div>

              {/* Sample Content */}
              <div className="space-y-3">
                <div
                  className="h-4 rounded"
                  style={{ 
                    backgroundColor: branding.branding_color_primary + '20',
                    border: `2px solid ${branding.branding_color_primary}`
                  }}
                ></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
              </div>

              {/* Footer Preview */}
              {branding.report_footer_text && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                    {branding.report_footer_text}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex justify-end space-x-4">
              <button
                onClick={fetchBranding}
                disabled={saving || uploading}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading || !branding.branding_company_name}
                className="px-6 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Branding'}
              </button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">How to use White-Label Reports</h4>
              <p className="text-xs text-blue-800 dark:text-blue-400">
                Once you've configured your branding, you can download white-labeled PDF reports from the Client Reports page. 
                Your custom logo, company name, colors, and footer will appear on all generated reports.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantBrandingSettings;
